import { useState } from 'react';
import { Type, X } from 'lucide-react';
import CustomAppBar from '../../../mainComponents/appBar';
import { useUserUpdateMutation } from '../../../redux/setting';
import toast from "react-hot-toast";

export default function HiddenWords() {
  const [input, setInput] = useState('');
  const [words, setWords] = useState<string[]>(() => {
    const saved = localStorage.getItem('hiddenWords');
    return saved ? JSON.parse(saved) : [];
  });
  const [updateNotificationSettings, { isLoading }] = useUserUpdateMutation();

  const updateWordsOnServer = async (updatedWords: string[]) => {
    try {
      const response = await updateNotificationSettings({
        
        unwanted_msg_list: updatedWords.join(','),
        
      }).unwrap();

      toast.success(response.message || "User Settings updated successfully");
    } catch (error) {
      console.error('Failed to update hidden words:', error);
      toast.error("Failed to update hidden words.");
    }
  };

  const addWord = async () => {
    const trimmed = input.trim().toLowerCase();
    if (trimmed && !words.includes(trimmed)) {
      const updatedWords = [...words, trimmed];
      setWords(updatedWords);
      setInput('');
      localStorage.setItem('hiddenWords', JSON.stringify(updatedWords));
      await updateWordsOnServer(updatedWords);
    }
  };

  const removeWord = async (word: string) => {
    const updatedWords = words.filter(w => w !== word);
    setWords(updatedWords);
    localStorage.setItem('hiddenWords', JSON.stringify(updatedWords));
    await updateWordsOnServer(updatedWords);
  };

  return (
    <div className="flex justify-center overflow-hidden no-scrollbar pt-10 bg-[#f5f5f5] h-screen px-4 py-6 text-black font-[SF Pro Display]">
      <div className="w-full max-w-sm">
        <CustomAppBar title="Hidden Words" />

        <div className="bg-white rounded-2xl mt-5 p-5 shadow-md space-y-4">
          <div className="flex items-start space-x-4">
            <div className="bg-blue-100 rounded-full p-2">
              <Type className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-[15px]">
                Automatically hide messages with specific words
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Add any words or phrases you don't want to see.
              </p>
            </div>
          </div>

          <div className="flex items-center mt-4 space-x-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addWord()}
              placeholder="Type a word and press enter"
              className="flex-1 bg-gray-100 rounded-xl px-4 py-2 text-sm focus:outline-none"
              disabled={isLoading}
            />
            <button
              onClick={addWord}
              disabled={isLoading || input.trim() === ''}
              className={`px-4 py-2 rounded-xl font-semibold text-sm ${
                isLoading || input.trim() === ''
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-black text-white'
              }`}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                'Add'
              )}
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {words.map((word, idx) => (
              <div
                key={idx}
                className={`flex items-center bg-gray-200 text-gray-800 text-sm rounded-full px-3 py-1 ${
                  isLoading ? 'opacity-50' : ''
                }`}
              >
                {word}
                <button
                  onClick={() => removeWord(word)}
                  disabled={isLoading}
                  className="ml-2 text-gray-600 hover:text-red-500 disabled:cursor-not-allowed"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500 italic pt-2">
            Messages with these words will be hidden from your inbox.
          </p>
        </div>
      </div>
    </div>
  );
}
